import { useState } from 'react';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-4 cursor-pointer"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center shadow-lg">
              <img
                src="https://static.readdy.ai/image/4f9829bb3ae345389ad4c282efba233c/e0de52b8400c4ce07cfdbea4cb7e414b.png"
                alt="Logo"
                className="h-10 w-auto"
              />
            </div>
            <div className={`transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              <div className="text-xl font-bold leading-tight">Fontanería El Rápido</div>
              <div className="text-xs opacity-80 uppercase tracking-wider">Servicio de urgencias 24 horas</div>
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={() => scrollToSection('servicios')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-[#FFA500] hover:bg-gray-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Servicios
            </button>

            <button
              onClick={() => scrollToSection('nosotros')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-[#FFA500] hover:bg-gray-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Nosotros
            </button>

            <button
              onClick={() => scrollToSection('galeria')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-[#FFA500] hover:bg-gray-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Galería
            </button>

            <button
              onClick={() => scrollToSection('testimonios')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-[#FFA500] hover:bg-gray-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Testimonios
            </button>

            <button
              onClick={() => scrollToSection('contacto')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-[#FFA500] hover:bg-gray-50'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Contacto
            </button>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+34640920362"
              className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black px-8 py-4 rounded-full text-sm font-bold hover:shadow-2xl hover:shadow-[#FFD700]/50 transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill"></i>
              </div>
              640 920 362
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-12 h-12 flex items-center justify-center rounded-xl transition-colors cursor-pointer ${
              scrolled
                ? 'text-gray-900 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={mobileMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-xl">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => scrollToSection('servicios')}
                className="text-left px-5 py-4 rounded-xl text-gray-700 hover:text-[#FFA500] hover:bg-gray-50 transition-all font-semibold cursor-pointer"
              >
                Servicios
              </button>

              <button
                onClick={() => scrollToSection('nosotros')}
                className="text-left px-5 py-4 rounded-xl text-gray-700 hover:text-[#FFA500] hover:bg-gray-50 transition-all font-semibold cursor-pointer"
              >
                Nosotros
              </button>

              <button
                onClick={() => scrollToSection('galeria')}
                className="text-left px-5 py-4 rounded-xl text-gray-700 hover:text-[#FFA500] hover:bg-gray-50 transition-all font-semibold cursor-pointer"
              >
                Galería
              </button>

              <button
                onClick={() => scrollToSection('testimonios')}
                className="text-left px-5 py-4 rounded-xl text-gray-700 hover:text-[#FFA500] hover:bg-gray-50 transition-all font-semibold cursor-pointer"
              >
                Testimonios
              </button>

              <button
                onClick={() => scrollToSection('contacto')}
                className="text-left px-5 py-4 rounded-xl text-gray-700 hover:text-[#FFA500] hover:bg-gray-50 transition-all font-semibold cursor-pointer"
              >
                Contacto
              </button>

              <a
                href="tel:+34640920362"
                className="mt-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black px-8 py-4 rounded-full text-sm font-bold hover:shadow-xl transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill"></i>
                </div>
                Llamar: 640 920 362
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
